import { useCallback, useRef } from 'react';

// Radix returns focus to a <DialogTrigger>; row-menu dialogs have none, so the
// opener (the menu trigger, handed in by the menu) is refocused on close. When
// the action moved the row out of view, its table takes focus instead of <body>.
// The table is only a landing spot: no outline, and the attributes added for it
// are removed once focus moves on.
function focusFallback(el: HTMLElement) {
  const addedTabIndex = !el.hasAttribute('tabindex');
  if (addedTabIndex) el.setAttribute('tabindex', '-1');
  el.classList.add('outline-none');
  el.addEventListener(
    'blur',
    () => {
      el.classList.remove('outline-none');
      if (addedTabIndex) el.removeAttribute('tabindex');
    },
    { once: true },
  );
  el.focus();
}

export function useMenuDialogFocus() {
  const returnFocus = useRef<HTMLElement | null>(null);
  const fallbackFocus = useRef<HTMLElement | null>(null);

  const rememberFocus = useCallback((opener?: HTMLElement | null) => {
    returnFocus.current = opener ?? null;
    fallbackFocus.current = opener?.closest<HTMLElement>('[data-slot="table"]') ?? null;
  }, []);

  const restoreFocus = useCallback((event: Event) => {
    const opener = returnFocus.current;
    const fallback = fallbackFocus.current;
    returnFocus.current = null;
    fallbackFocus.current = null;
    const target = opener?.isConnected ? opener : fallback?.isConnected ? fallback : null;
    if (!target) return;
    event.preventDefault();
    if (target === fallback) focusFallback(target);
    else target.focus();
  }, []);

  return { rememberFocus, restoreFocus };
}
